// The per-conversation cap on browser notifications and the «y N mensajes más» digest past it — the
// Mac's `MacAttentionService` rule (5 banners per conversation, then ONE summary keyed per channel,
// updated in place, quoting the last 5 lines with their senders; Android's MessagingStyle summary +
// `SUMMARY_HISTORY_LIMIT`). The web has no "pass": the burst is everything delivered for that
// conversation since it was last read (withdraw-on-read resets it, as on the Mac).
//
// Pure: the page keeps one `NotificationDigest` and asks it what to draw for each message.
import { tf } from '../i18n/i18n';

export const NotificationDigestRules = {
  /** Banners per conversation before the summary takes over (Mac `maxBannersPerChannelPerPass`). */
  maxBannersPerChannel: 5,
  /** Transcript lines the summary quotes (Mac `digestHistoryLimit`, Android `SUMMARY_HISTORY_LIMIT`). */
  historyLimit: 5,
};

export type DigestDecision =
  /** Draw this message as its own notification (tag = the post id). */
  | { kind: 'banner'; tag: string }
  /** Replace the conversation's summary (tag `summary:<channel>`) with this body. */
  | { kind: 'summary'; tag: string; body: string; overflow: number };

/** The summary's count line, the Mac's `Strings.Mac.digestCount`. */
export function digestCountLine(count: number): string {
  return tf(count === 1 ? 'mac.digest_one' : 'mac.digest_many', count);
}

export class NotificationDigest {
  private readonly delivered = new Map<string, number>();
  private readonly history = new Map<string, string[]>();

  /** One notifiable message. `line` is ALWAYS sender-attributed (the digest quotes people even in a DM). */
  note(channelId: string, postId: string, line: string): DigestDecision {
    const lines = [...(this.history.get(channelId) ?? []), line];
    if (lines.length > NotificationDigestRules.historyLimit) lines.splice(0, lines.length - NotificationDigestRules.historyLimit);
    this.history.set(channelId, lines);
    const count = (this.delivered.get(channelId) ?? 0) + 1;
    this.delivered.set(channelId, count);
    if (count <= NotificationDigestRules.maxBannersPerChannel) return { kind: 'banner', tag: postId };
    const overflow = count - NotificationDigestRules.maxBannersPerChannel;
    return { kind: 'summary', tag: NotificationDigest.summaryTag(channelId), body: [digestCountLine(overflow), ...lines].join('\n'), overflow };
  }

  /** The conversation was read (or its notifications taken down): the burst starts again. */
  withdraw(channelId: string): void {
    this.delivered.delete(channelId);
    this.history.delete(channelId);
  }

  static summaryTag(channelId: string): string {
    return `summary:${channelId}`;
  }
}
